/* ============================================================
   Tafra — ui.js
   Progressive-enhancement behaviours that operate on the
   already-rendered DOM:
     • scroll reveals            [data-reveal]
     • animated counters         [data-count]
     • filter groups             [data-filter-group] + [data-filterable]
     • product gallery           [data-gallery]
     • FAQ accordion             [data-accordion]
   All are optional per page — each block no-ops if absent.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* ---- Scroll reveals ------------------------------------- */
  (function reveals() {
    // The hero runs its own CSS-only entrance (see base.css) so its LCP text is
    // never hidden behind this script.
    var els = [].slice.call(document.querySelectorAll('[data-reveal]')).filter(function (el) {
      return !el.closest('.hero');
    });
    if (!els.length) return;
    if (reduceMotion || !hasIO) return; // leave fully visible
    document.documentElement.classList.add('reveal-ready');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var sibs = el.parentElement
          ? [].slice.call(el.parentElement.querySelectorAll(':scope > [data-reveal]'))
          : [el];
        var idx = Math.max(0, sibs.indexOf(el));
        el.style.animationDelay = Math.min(idx * 110, 680) + 'ms';
        el.classList.add('is-visible');
        io.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    els.forEach(function (el) { io.observe(el); });
  })();

  /* ---- Logo marquee --------------------------------------- */
  /* The logos are held back until every one has decoded, so the
     strip fades in complete instead of filling in slot by slot. */
  (function marquees() {
    var strips = [].slice.call(document.querySelectorAll('[data-marquee]'));
    if (!strips.length) return;
    document.documentElement.classList.add('marquee-ready');

    strips.forEach(function (strip) {
      var imgs = [].slice.call(strip.querySelectorAll('img'));
      var pending = imgs.length;
      var timer;

      function reveal() {
        clearTimeout(timer);
        strip.classList.add('is-loaded');
      }

      function settle() {
        if (--pending <= 0) reveal();
      }

      if (!pending) { reveal(); return; }
      // never leave the strip blank if a logo 404s or stalls
      timer = setTimeout(reveal, 2500);

      imgs.forEach(function (img) {
        if (img.complete) { settle(); return; }
        img.addEventListener('load', settle, { once: true });
        img.addEventListener('error', settle, { once: true });
      });
    });
  })();

  /* ---- Animated counters ---------------------------------- */
  /* Counters hold at 0 until their section is scrolled into
     view, then every number in that section animates together. */
  (function counters() {
    var nums = [].slice.call(document.querySelectorAll('[data-count]'));
    if (!nums.length) return;

    function fmt(target, v) {
      return target >= 1000 ? Math.round(v).toLocaleString() : Math.round(v).toString();
    }
    function run(el) {
      var target = parseFloat(el.getAttribute('data-count'));
      var suffix = el.getAttribute('data-suffix') || '';
      if (reduceMotion) { el.textContent = fmt(target, target) + suffix; return; }
      var dur = 1200, start = performance.now();
      function tick(now) {
        var t = Math.min(1, (now - start) / dur);
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = fmt(target, target * eased) + suffix;
        if (t < 1) requestAnimationFrame(tick);
        else el.textContent = fmt(target, target) + suffix;
      }
      requestAnimationFrame(tick);
    }

    // group each counter with the others in its section
    function sectionOf(el) { return el.closest('section') || el.parentElement; }

    if (!hasIO) { nums.forEach(function (n) { run(n); }); return; }

    var groups = [];
    nums.forEach(function (n) {
      var sec = sectionOf(n);
      var g = null;
      for (var i = 0; i < groups.length; i++) { if (groups[i].sec === sec) { g = groups[i]; break; } }
      if (!g) { g = { sec: sec, items: [] }; groups.push(g); }
      g.items.push(n);
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        groups.forEach(function (g) {
          if (g.sec === e.target) g.items.forEach(run);
        });
      });
    }, { threshold: 0.2 });

    groups.forEach(function (g) { io.observe(g.sec); });
  })();

  /* ---- Filter groups (category products) ------------------ */
  /* <div data-filter-group>
        <button class="chip" data-filter="All">…</button> …
     </div>
     items: [data-filterable data-type="…"] anywhere on the page.
     A container may declare data-filter-target to scope items. */
  [].slice.call(document.querySelectorAll('[data-filter-group]')).forEach(function (group) {
    var buttons = [].slice.call(group.querySelectorAll('[data-filter]'));
    var scope = group.getAttribute('data-filter-target');
    var root = scope ? document.querySelector(scope) : document;
    var items = [].slice.call(root.querySelectorAll('[data-filterable]'));
    var search = scope ? null : null;

    function apply(value) {
      items.forEach(function (item) {
        var type = item.getAttribute('data-type') || '';
        item.hidden = !(value === 'All' || type === value);
      });
      buttons.forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-filter') === value);
      });
      // let a paired search re-filter on top, if present
      group.dispatchEvent(new CustomEvent('filterchange', { detail: value }));
    }

    group.addEventListener('click', function (e) {
      var b = e.target.closest('[data-filter]');
      if (b) apply(b.getAttribute('data-filter'));
    });
    // init to the active chip (or first)
    var initial = group.querySelector('[data-filter].is-active') || buttons[0];
    if (initial) apply(initial.getAttribute('data-filter'));
  });

  /* ---- Product gallery ------------------------------------ */
  /* [data-gallery] wraps a main img [data-gallery-main] and
     thumb buttons [data-gallery-thumb] whose data-src is the
     image to show. */
  [].slice.call(document.querySelectorAll('[data-gallery]')).forEach(function (gal) {
    var main = gal.querySelector('[data-gallery-main]');
    var thumbs = [].slice.call(gal.querySelectorAll('[data-gallery-thumb]'));
    if (!main) return;
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-src') || (thumb.querySelector('img') && thumb.querySelector('img').src);
        if (src) main.src = src;
        thumbs.forEach(function (t) {
          var active = t === thumb;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      });
    });
  });

  /* ---- FAQ accordion -------------------------------------- */
  /* [data-accordion] with buttons [data-accordion-trigger] whose
     next sibling [data-accordion-panel] collapses. */
  [].slice.call(document.querySelectorAll('[data-accordion]')).forEach(function (acc) {
    var triggers = [].slice.call(acc.querySelectorAll('[data-accordion-trigger]'));
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        triggers.forEach(function (t) {
          var open = t === trigger && !isOpen;
          t.setAttribute('aria-expanded', open ? 'true' : 'false');
          var panel = t.nextElementSibling;
          if (panel) panel.style.maxHeight = open ? panel.scrollHeight + 'px' : '0px';
        });
      });
    });
    // open the first item by default
    if (triggers[0]) triggers[0].click();
  });

  /* ---- Contact form: guard the submit --------------------- */
  /* Native validation runs before this fires, so all that is left to check is
     whether Turnstile has handed the form its token yet. Without that check the
     visitor waits through a round trip only to be sent back with a captcha
     error. Once it passes, the button locks so nothing is sent twice. */
  [].slice.call(document.querySelectorAll('form[data-contact-form]')).forEach(function (form) {
    var button = form.querySelector('[data-submit]');
    var spinner = button && button.querySelector('[data-submit-spinner]');
    var turnstileError = form.querySelector('[data-turnstile-error]');

    function token() {
      var input = form.querySelector('[name="cf-turnstile-response"]');
      return input && input.value ? input.value.trim() : '';
    }

    // Clear the warning as soon as the widget resolves.
    form.addEventListener('change', function (e) {
      if (turnstileError && e.target && e.target.name === 'cf-turnstile-response') {
        turnstileError.hidden = true;
      }
    }, true);

    form.addEventListener('submit', function (e) {
      if (!token()) {
        e.preventDefault();
        if (turnstileError) {
          turnstileError.hidden = false;
          try {
            turnstileError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch (err) {}
        }
        return;
      }

      if (turnstileError) turnstileError.hidden = true;
      if (!button || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      if (spinner) spinner.hidden = false;
    });
  });

  /* ---- Gentle hero parallax ------------------------------- */
  /* The hero photo drifts slightly slower than the scroll for a
     calm sense of depth. Bounded, rAF-batched, and disabled for
     reduced-motion users. Opt-in extras can use [data-parallax]. */
  (function parallax() {
    if (reduceMotion) return;
    var layers = [].slice.call(document.querySelectorAll('.hero__image, [data-parallax]'));
    if (!layers.length) return;

    var ticking = false;
    function update() {
      var y = window.scrollY || window.pageYOffset;
      layers.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.06;
        var shift = Math.max(-60, Math.min(60, y * speed));
        el.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  })();
})();

/* ============================================================
   Tafra — motion & interaction layer (independent of the block
   above). Injects a scroll-progress bar and a back-to-top FAB,
   then wires cursor spotlight, magnetic buttons, and 3D tilt.
   Every effect is pointer-driven and skipped for reduced-motion
   / coarse-pointer (touch) users so nothing feels janky on phones.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer  = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---- Scroll progress + back-to-top ---------------------- */
  (function scrollChrome() {
    var progress = document.createElement('div');
    progress.className = 'scroll-progress';
    progress.setAttribute('aria-hidden', 'true');
    var bar = document.createElement('div');
    bar.className = 'scroll-progress__bar';
    progress.appendChild(bar);

    var toTop = document.createElement('button');
    toTop.className = 'to-top';
    toTop.type = 'button';
    toTop.setAttribute('aria-label', 'Back to top');
    toTop.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';

    // Always-on WhatsApp button (pulses; rides above the arrow). The number
    // comes from the settings row via <meta name="whatsapp-url">; with none
    // configured the button is not built at all rather than pointing nowhere.
    var waUrl = (document.querySelector('meta[name="whatsapp-url"]') || {}).content || '';
    var wa = document.createElement('a');
    wa.className = 'wa-fab';
    wa.href = waUrl;
    wa.target = '_blank';
    wa.rel = 'noopener';
    wa.setAttribute('aria-label', 'Chat on WhatsApp');
    wa.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.5 14.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.09 3.2 5.07 4.48.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35zM12.05 21.5h-.01a9.5 9.5 0 0 1-4.84-1.33l-.35-.2-3.6.94.96-3.51-.23-.36a9.46 9.46 0 0 1-1.45-5.04c0-5.24 4.27-9.5 9.52-9.5 2.54 0 4.93.99 6.73 2.79a9.44 9.44 0 0 1 2.79 6.72c0 5.24-4.27 9.5-9.52 9.5zm8.1-17.6A11.4 11.4 0 0 0 12.05.5C5.76.5.64 5.62.64 11.91c0 2.02.53 3.99 1.53 5.73L.5 23.5l6.02-1.58a11.36 11.36 0 0 0 5.53 1.41h.01c6.29 0 11.41-5.12 11.41-11.41 0-3.05-1.19-5.91-3.35-8.07z"/></svg>';

    document.body.appendChild(progress);
    document.body.appendChild(toTop);
    if (waUrl) document.body.appendChild(wa);

    var ticking = false;
    function update() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - doc.clientHeight;
      var y = window.scrollY || window.pageYOffset;
      var ratio = max > 0 ? Math.min(1, y / max) : 0;
      bar.style.transform = 'scaleX(' + ratio.toFixed(4) + ')';
      var showArrow = y > 600;
      toTop.classList.toggle('is-visible', showArrow);
      wa.classList.toggle('is-raised', showArrow); // WhatsApp lifts above the arrow
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();

    toTop.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  })();

  // pointer-driven flourishes are desktop-only and motion-safe
  if (reduceMotion || !finePointer) return;

  /* ---- Cursor spotlight on cards -------------------------- */
  (function spotlight() {
    var cards = [].slice.call(document.querySelectorAll('.card--hover'));
    if (!cards.length) return;
    cards.forEach(function (card) {
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        card.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100) + '%');
        card.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100) + '%');
      });
    });
  })();

  /* ---- Magnetic buttons ----------------------------------- */
  /* Primary CTAs and white buttons gently drift toward the
     cursor, then spring back on leave. */
  (function magnetic() {
    var btns = [].slice.call(document.querySelectorAll(
      '.btn--primary.btn--lg, .btn--white, .to-top'
    ));
    btns.forEach(function (btn) {
      if (btn.classList.contains('to-top')) return; // FAB keeps its own transform
      btn.setAttribute('data-magnetic', '');
      var strength = 0.32;
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var dx = e.clientX - (r.left + r.width / 2);
        var dy = e.clientY - (r.top + r.height / 2);
        btn.style.setProperty('--mgx', (dx * strength).toFixed(1) + 'px');
        btn.style.setProperty('--mgy', (dy * strength - 2).toFixed(1) + 'px');
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.setProperty('--mgx', '0px');
        btn.style.setProperty('--mgy', '0px');
      });
    });
  })();

  /* ---- 3D tilt on [data-tilt] ----------------------------- */
  (function tilt() {
    var els = [].slice.call(document.querySelectorAll('[data-tilt]'));
    if (!els.length) return;
    els.forEach(function (el) {
      var max = parseFloat(el.getAttribute('data-tilt')) || 6; // degrees
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.style.setProperty('--try', (px * max).toFixed(2) + 'deg');
        el.style.setProperty('--trx', (-py * max).toFixed(2) + 'deg');
      });
      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--trx', '0deg');
        el.style.setProperty('--try', '0deg');
      });
    });
  })();
})();
